import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import Typography from '../../../../components/common/Typography/index'
import Card from '../../../../components/common/Card/index'
import MiniHeader from '../../../../components/layout/MiniHeader'

import useAppSelector from '../../../../state/redux/hooks/index'
import { fetchCustomers } from '../../../../state/redux/actions/customerActions'
import ErrorState from '../../../../components/state/ErrorState'
import LoadingIndicator from '../../../../components/state/LoadingIndicator'
import { Link } from 'react-router-dom'
import { List, ListItem, ListItemButton, ListItemAvatar, ListItemText, Avatar } from '@mui/material'
import { formatDateDashed } from '../../../../shared/utils/dateUtils'

const CustomerList = () => {
  const [dateFilter, setDateFilter] = useState(null)
  const [isFiltering, setIsFiltering] = useState(false)
  const dispatch = useDispatch()
  const { customers, loading, error } = useAppSelector(state => state.customer)

  useEffect(() => {
    dispatch(fetchCustomers())
  }, [dispatch])

  // Filter customers by date range
  const filterCustomersByDate = (customerList, startDate, endDate) => {
    if (!startDate || !endDate || !customerList || !Array.isArray(customerList)) {
      return customerList || []
    }

    return customerList.filter(customer => {
      const customerDate = new Date(customer.createdAt)
      const start = new Date(startDate)
      const end = new Date(endDate)

      // Set time boundaries
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)

      return customerDate >= start && customerDate <= end
    })
  }

  // Apply date filter if active, otherwise use all customers
  const filteredCustomers = isFiltering && dateFilter
    ? filterCustomersByDate(customers, dateFilter.startDate, dateFilter.endDate)
    : customers

  // Handle filter change from MiniHeader
  const handleMiniHeaderFilterChange = (filtered, dateRange) => {
    if (!dateRange) {
      // No date filter applied - reset to show all data
      setIsFiltering(false)
      setDateFilter(null)
      return
    }

    // Date filter is applied - set filtering state
    setIsFiltering(true)
    setDateFilter(dateRange)
  }

  return (
    <>
      <MiniHeader
        title="User Management"
        data={customers}
        onFilterChange={handleMiniHeaderFilterChange}
      />
      <div className="ml-[250px] flex-1 p-8 w-[1040px] mx-auto my-0">
        {loading && <LoadingIndicator />}
        {!loading && error && <ErrorState error={error} />}
        {!loading && !error && filteredCustomers.length === 0 && (
          <Card
            variant="outlined"
            className="text-center p-8 border border-border rounded-md max-w-md w-full mt-[140px]"
          >
            <Typography variant="body" className="text-secondary">
              No teams found. Add new teams to manage accounts.
            </Typography>
          </Card>
        )}
        {!loading && !error && filteredCustomers.length > 0 && (
          <>
            <div className="flex justify-start gap-4 p-4">
              {/* total customer */}
              <Card className="mt-[4px] w-[200px]">
                <div className="flex flex-col p-4">
                  <Typography>Total Customers</Typography>
                  <Typography>{filteredCustomers.length}</Typography>
                </div>
              </Card>
              {/* active customer */}
              <Card className="mt-[4px] w-[200px]">
                <div className="flex flex-col p-4">
                  <Typography>Active</Typography>
                  <div className="flex justify-between ">
                    <Typography>{`${filteredCustomers.filter(customer => customer.status === 'ACTIVE').length}`}</Typography>
                    <div className="flex">
                      <Typography variant="body">
                        {Math.round(
                          (filteredCustomers.filter(customer => customer.status === 'ACTIVE').length / filteredCustomers.length) * 100
                        )}
                        %
                      </Typography>
                      <i className="fa-solid fa-arrow-up text-green-500" />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            <div className="bg-white">
              <List className="">
                <ListItem className="grid grid-cols-6 gap-4">
                  <ListItemText
                    primary={
                      <Typography variant="h5" align="" className="">
                        Name
                      </Typography>
                    }
                    className="col-span-2"
                  />
                  <ListItemText
                    primary={
                      <Typography variant="h5" align="" className="">
                        Email
                      </Typography>
                    }
                    className=""
                  />
                  <ListItemText
                    primary={
                      <Typography variant="h5" align="" className="">
                        Region
                      </Typography>
                    }
                    className=""
                  />
                  <ListItemText
                    primary={
                      <Typography variant="h5" align="" className="">
                        Sign Up Date
                      </Typography>
                    }
                    className=""
                  />
                  <ListItemText
                    primary={
                      <Typography variant="h5" align="" className="">
                        Download Store
                      </Typography>
                    }
                    className=""
                  />
                </ListItem>
                {filteredCustomers.map(customer => (
                  <Link key={customer._id} to={`/users/customers/${customer._id}`}>
                    <ListItemButton className="grid grid-cols-6 gap-4">
                      <div className="flex col-span-2">
                        <ListItemAvatar>
                          <Avatar alt="Profile Picture" src={customer.image} />
                        </ListItemAvatar>
                        <ListItemText align="left">
                          <Typography variant="h5" className="font-normal">
                            {customer.full_name}
                          </Typography>
                        </ListItemText>
                      </div>
                      <ListItemText>
                        <Typography variant="caption" className="">
                          {customer.email.length > 15 ? `${customer.email.slice(0, 15)}...` : customer.email}
                        </Typography>
                      </ListItemText>
                      <ListItemText className="">
                        <Typography variant="h5" className="font-normal">
                          {customer.region}
                        </Typography>
                      </ListItemText>
                      <ListItemText>
                        <Typography variant="caption" className="">
                          {formatDateDashed(customer?.signupDate)}
                        </Typography>
                      </ListItemText>
                      <ListItemText>
                        <Typography variant="h5" className="font-normal">
                          {customer.device_type && customer.device_type === 'android' ? 'Google Play' : 'App Store'}
                        </Typography>
                      </ListItemText>
                    </ListItemButton>
                  </Link>
                ))}
              </List>
            </div>
            {/* <div className="grid gap-6 grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
              <Card
                variant="outlined"
                className="p-6 transition-transform duration-200 ease-in-out hover:-translate-y-0.5"
              >
                <div className="mb-4">
                  <Typography variant="h5" className="font-semibold text-blue-500">
                    {customers.name}
                  </Typography>
                  <Typography variant="caption" className="text-secondary mt-2">
                    {customers.email}
                  </Typography>
                </div>

                <div className="">
                  <Typography variant="body" className="mb-2 text-gray-700">
                    <strong>Role:</strong> {customers.role}
                  </Typography>
                  <Typography variant="body" className="mb-2 text-gray-700">
                    <strong>Status:</strong> {customers.status}
                  </Typography>
                </div>

                <div className="flex justify-end gap-4 mt-4">
                  <button className="text-sm whitespace-nowrap text-danger border border-border mt-2 text-left bg-transparent py-3 px-6 w-full inline-block cursor-pointer hover:bg-slate-100">
                    {' '}
                    Edit
                  </button>
                  <button className="text-sm whitespace-nowrap text-danger border border-border mt-2 text-left bg-transparent py-3 px-6 w-full inline-block cursor-pointer hover:bg-slate-100">
                    {' '}
                    Delete
                  </button>
                </div>
              </Card>
            </div> */}
          </>
        )}
      </div>
    </>
  )
}

export default CustomerList
